<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { inboxConnectionStatus } from 'dashboard/composables/useInboxConnectionHealth';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { accountScopedRoute } = useAccount();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');
const showOnlyProblems = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');

const isDisconnected = inbox =>
  inboxConnectionStatus(inbox) === 'disconnected';

// Caixa desconectada vem primeiro: é a que pede ação de quem abre a lista.
const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => {
    const byHealth = Number(isDisconnected(b)) - Number(isDisconnected(a));
    return byHealth || a.name.localeCompare(b.name);
  });
});

const problemCount = computed(
  () => (inboxesList.value || []).filter(isDisconnected).length
);

// Se a última caixa com problema reconectar, o filtro deixa de valer sozinho.
const isProblemFilterActive = computed(
  () => showOnlyProblems.value && problemCount.value > 0
);

const filteredInboxesList = computed(() => {
  const list = isProblemFilterActive.value
    ? inboxesList.value.filter(isDisconnected)
    : inboxesList.value;
  const query = searchQuery.value.trim();
  if (!query) return list;
  return picoSearch(list, query, ['name', 'channel_type']);
});

const toggleProblemFilter = () => {
  showOnlyProblems.value = !isProblemFilterActive.value;
};

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="problemCount" #tabs>
          <Button
            :label="$t('INBOX_MGMT.FILTER_PROBLEMS', { n: problemCount })"
            icon="i-ph-warning-circle"
            size="sm"
            :variant="isProblemFilterActive ? 'faded' : 'outline'"
            :color="isProblemFilterActive ? 'ruby' : 'slate'"
            :aria-pressed="isProblemFilterActive"
            @click="toggleProblemFilter"
          />
        </template>
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-2xl size-10 ring ring-n-solid-1 border border-n-strong grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-2xl ring ring-n-solid-1 border border-n-strong grid place-items-center"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="flex flex-col items-start gap-1">
              <span class="block text-heading-3 text-n-slate-12">
                {{ inbox.name }}
              </span>
              <div class="flex items-center gap-2">
                <ChannelName
                  :channel-type="inbox.channel_type"
                  :medium="inbox.medium"
                  :voice-enabled="inbox.voice_enabled"
                  class="text-body-main text-n-slate-11"
                />
                <router-link
                  v-if="
                    inboxConnectionStatus(inbox) === 'disconnected' && isAdmin
                  "
                  :to="
                    accountScopedRoute('settings_inbox_show', {
                      inboxId: inbox.id,
                      tab: 'configuration',
                    })
                  "
                  class="inline-flex items-center gap-1 text-xs font-medium text-n-ruby-11 hover:text-n-ruby-10"
                  :aria-label="
                    $t('INBOX_CONNECTION_HEALTH.STATUS_DISCONNECTED')
                  "
                >
                  <span class="size-1.5 rounded-full bg-n-ruby-9" />
                  {{ $t('INBOX_CONNECTION_HEALTH.STATUS_DISCONNECTED') }}
                </router-link>
                <span
                  v-else-if="inboxConnectionStatus(inbox) === 'disconnected'"
                  class="inline-flex items-center gap-1 text-xs font-medium text-n-ruby-11"
                  :aria-label="
                    $t('INBOX_CONNECTION_HEALTH.STATUS_DISCONNECTED')
                  "
                >
                  <span class="size-1.5 rounded-full bg-n-ruby-9" />
                  {{ $t('INBOX_CONNECTION_HEALTH.STATUS_DISCONNECTED') }}
                </span>
                <span
                  v-else-if="inboxConnectionStatus(inbox) === 'connected'"
                  class="inline-flex items-center gap-1 text-xs font-medium text-n-slate-11"
                  :aria-label="$t('INBOX_CONNECTION_HEALTH.STATUS_CONNECTED')"
                >
                  <span class="size-1.5 rounded-full bg-n-slate-8" />
                  {{ $t('INBOX_CONNECTION_HEALTH.STATUS_CONNECTED') }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex gap-3 justify-end">
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                :aria-label="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              :aria-label="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
